/**
 * 시점 비교 server component (Phase S Step 2, 2026-06-05).
 *
 * KeywordMonthlySnapshot 기반 — 30·60·90·180·365일 전 시점 metric 비교.
 * 데이터 부족 시점 = 자동 분기 (해당 시점 표시 X + 안내).
 *
 * SEO 효과 — server-rendered 본문 + 시점별 키워드 데이터 = 진짜 트렌드 분석 보고서.
 *
 * 2026-09-20 모바일 가독성 재설계 (사용자 결정 — 시안 C):
 *   옛 8열 (시점 + 3지표 × 값·변화 + 단계) → 390px 폭에서 열당 ~40px, 한글 세로 쪼개짐.
 *   신 4열 (시점 / 30일 언급 / 트렌드점수 / 검색지수) — 변화율은 값 아래 한 줄,
 *   단계·snapshot 날짜·실제 일수는 시점 셀 부제 한 줄. 모든 셀 nowrap + tabular-nums.
 *   비교 기준은 항상 "현재 (오늘 keyword_rolling) vs 그 시점 snapshot" — 부제에 snapshot 날짜 명시.
 */

interface SnapshotMetric {
  mention_count_30d: number;
  mention_count_7d: number;
  normalized_score: number | null;
  score_30d: number | null;
  lifecycle_stage: string | null;
  lifecycle_substage: string | null;
  datalab_index?: number | null;          // Phase S Step 4 v2
}

interface ComparisonItem {
  label: string;                          // "지난달 (5월)" 같이 월별 직관
  days_ago: number;
  snapshot_date: string;
  actual_days_ago: number;
  month_label?: string;                   // Phase S Step 4 v2 — "5월" 단독
  snapshot_metric: SnapshotMetric;
  change: {
    mention_count_30d_pct: number | null;
    normalized_score_pct: number | null;
    datalab_index_pct?: number | null;    // Phase S Step 4 v2
    stage_changed: boolean;
    stage_from: string | null;
    stage_to: string | null;
  };
}

interface DataStatus {
  available_snapshots: string[];
  total_months: number;
  earliest: string | null;
  latest: string | null;
}

export interface SnapshotComparisonData {
  keyword_id: number;
  keyword: string;
  current: SnapshotMetric | null;
  comparisons: ComparisonItem[];
  data_status: DataStatus;
}

const STAGE_KR: Record<string, string> = {
  emerging: "태동기", growing: "성장기", mature: "성숙기", declining: "하락기",
};

function _changeColor(pct: number | null): string {
  if (pct === null) return "text-text-muted";
  if (pct >= 10) return "text-emerald-400";
  if (pct >= 0) return "text-emerald-500/70";
  if (pct >= -10) return "text-amber-400";
  return "text-rose-400";
}

function _changeIcon(pct: number | null): string {
  if (pct === null) return "—";
  if (pct >= 5) return "▲";
  if (pct <= -5) return "▼";
  return "—";
}

function _fmtPct(pct: number | null): string {
  if (pct === null) return "—";
  const abs = Math.abs(pct).toFixed(1);
  // ±5% 미만은 아이콘 "—" 라 부호만 남김
  const icon = _changeIcon(pct);
  if (icon === "—") return `${pct > 0 ? "+" : pct < 0 ? "-" : ""}${abs}%`;
  return `${icon} ${abs}%`;
}

/** "2026-08-01" → "8/1" (같은 해) / "2025/12/1" (다른 해) */
function _fmtSnapDate(iso: string, today: Date): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return y === today.getFullYear() ? `${m}/${d}` : `${y}/${m}/${d}`;
}

/** 값 + 아래 변화율 — 과거 행 셀 */
function MetricCell({ value, pct }: { value: string; pct: number | null }) {
  return (
    <td className="px-1.5 py-2 text-right align-top border-b border-border/50 whitespace-nowrap">
      <span className="block text-sm font-medium text-text-sub tabular-nums leading-tight">{value}</span>
      <span className={`block text-2xs font-bold tabular-nums mt-0.5 ${_changeColor(pct)}`}>{_fmtPct(pct)}</span>
    </td>
  );
}

export function SnapshotComparison({ data }: { data: SnapshotComparisonData | null }) {
  if (!data || !data.current) return null;

  const hasComparisons = data.comparisons.length > 0;
  const today = new Date();

  return (
    <section className="mt-6 px-4">
      <article className="bg-bg-elevated border border-border rounded-lg p-4">
        <h2 className="text-base font-bold text-text mb-0.5">시점 비교</h2>

        {!hasComparisons ? (
          /* 데이터 부족 안내 */
          <div className="bg-bg border-l-4 border-amber-500 p-3 rounded text-sm mt-2">
            <p className="text-text">데이터 누적 중</p>
            <p className="text-xs text-text-muted mt-1">
              매월 1일 키워드 metric snapshot 자동 저장 → 시점 비교 가능.
              현재 snapshot: <strong>{data.data_status.total_months}개</strong>
              {data.data_status.latest && <> (최신 {data.data_status.latest})</>}.
              30일 전 비교는 1개월 후 활성됩니다.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-text-muted mb-3">
              매월 1일 snapshot 기준 · 변화율은 모두 <span className="text-text-sub">현재 대비</span>
            </p>
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[22%]" />
                <col className="w-[22%]" />
                <col className="w-[22%]" />
              </colgroup>
              <thead>
                <tr className="text-2xs font-medium text-text-muted">
                  <th className="text-left pb-1.5 border-b border-border whitespace-nowrap">시점</th>
                  <th className="text-right pb-1.5 px-1.5 border-b border-border whitespace-nowrap">30일 언급</th>
                  <th className="text-right pb-1.5 px-1.5 border-b border-border whitespace-nowrap">트렌드점수</th>
                  <th className="text-right pb-1.5 px-1.5 border-b border-border whitespace-nowrap">검색지수</th>
                </tr>
              </thead>
              <tbody>
                {/* 현재 행 — 비교 기준점. 흰색 굵게 + primary 배경 */}
                <tr className="bg-primary/5">
                  <td className="py-2 pl-1.5 align-top border-b border-border/50 whitespace-nowrap rounded-l">
                    <span className="block text-xs font-bold text-primary leading-tight">현재</span>
                    <span className="block text-2xs text-text-muted mt-0.5">
                      {STAGE_KR[data.current.lifecycle_stage || ""] || "—"}
                    </span>
                  </td>
                  <td className="px-1.5 py-2 text-right align-top border-b border-border/50 whitespace-nowrap">
                    <span className="block text-sm font-bold text-text tabular-nums leading-tight">{data.current.mention_count_30d.toLocaleString()}</span>
                  </td>
                  <td className="px-1.5 py-2 text-right align-top border-b border-border/50 whitespace-nowrap">
                    <span className="block text-sm font-bold text-text tabular-nums leading-tight">{data.current.normalized_score?.toFixed(1) ?? "—"}</span>
                  </td>
                  <td className="px-1.5 py-2 text-right align-top border-b border-border/50 whitespace-nowrap rounded-r">
                    <span className="block text-sm font-bold text-text tabular-nums leading-tight">{data.current.datalab_index?.toFixed(1) ?? "—"}</span>
                  </td>
                </tr>

                {data.comparisons.map((c) => {
                  const stageThen = STAGE_KR[c.snapshot_metric.lifecycle_stage || ""] || "—";
                  const stageNow = STAGE_KR[c.change.stage_to || ""] || "";
                  const stageText = c.change.stage_changed && stageNow ? `${stageThen}→${stageNow}` : stageThen;
                  return (
                    <tr key={c.days_ago}>
                      <td className="py-2 pl-1.5 align-top border-b border-border/50">
                        {/* 라벨만 wrap 허용 ("반년 전 (6개월) (3월)" 같은 긴 라벨) — 부제 2줄은 nowrap */}
                        <span className="block text-xs font-bold text-text leading-tight">{c.label}</span>
                        <span
                          className="block text-2xs text-text-muted mt-0.5 whitespace-nowrap"
                          title={`snapshot ${c.snapshot_date} (${c.actual_days_ago}일 전) 대비 현재 변화`}
                        >
                          {_fmtSnapDate(c.snapshot_date, today)} · {c.actual_days_ago}일 전
                        </span>
                        <span className={`block text-2xs whitespace-nowrap ${c.change.stage_changed ? "text-amber-400" : "text-text-muted"}`}>
                          {stageText}
                        </span>
                      </td>
                      <MetricCell value={c.snapshot_metric.mention_count_30d.toLocaleString()} pct={c.change.mention_count_30d_pct} />
                      <MetricCell value={c.snapshot_metric.normalized_score?.toFixed(1) ?? "—"} pct={c.change.normalized_score_pct} />
                      <MetricCell value={c.snapshot_metric.datalab_index?.toFixed(1) ?? "—"} pct={c.change.datalab_index_pct ?? null} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-2xs text-text-muted mt-2">
              데이터 시작 {data.data_status.earliest} · 누적 snapshot {data.data_status.total_months}개월
            </p>
          </>
        )}
      </article>
    </section>
  );
}
